import React, {useState} from "react";

import "./Program.detail.scss";

import {useQuery, useLazyQuery, useMutation} from "@apollo/client";
import gqlSchemaLibrary from "../../services/graphql.schemas";
import {Link, useNavigate, useParams} from "react-router-dom";
import TrackerEntry from "./Program.detail.trackerEntry";
import Select from "../../forms/inputs/Select";
import SpinnerLoader from "../../visuals/SpinnerLoader";
import ItemMenu from "../../forms/menu/MenuComponent";
import ButtonIcon from "../../forms/buttons/Button.icon";
import ProgramReportDialog from "../dialogs/Program.report.dialog";
import AlertsReportDialog from "../dialogs/Alerts.report.dialog";
import {useTranslation} from "react-i18next";
import moment from "moment";
import { Tooltip as ReactTooltip } from "react-tooltip";
import {PageViewLog} from "../../services/interactions/page.view";
import {PAGES} from "../../services/interactions/audit.trail";
import {TooltipLog} from "../../services/interactions/elements/tooltip";

function ScheduleEntry({
                           index,
                           item,
                           labels,
                           oneRow,
                           options,
                           options2,
                           onChangeClinician,
                           changeSchedule,
                       }) {
    const {t} = useTranslation(["programs", "common", "participants"]);
    const getDayLabel = () => {
        let dayLabel = "";
        switch (index) {
            case 0:
                dayLabel = t("common:week.sun").toUpperCase();
                break;
            case 1:
                dayLabel = t("common:week.mon").toUpperCase();
                break;
            case 2:
                dayLabel = t("common:week.tue").toUpperCase();
                break;
            case 3:
                dayLabel = t("common:week.wed").toUpperCase();
                break;
            case 4:
                dayLabel = t("common:week.thu").toUpperCase();
                break;
            case 5:
                dayLabel = t("common:week.fri").toUpperCase();
                break;
            case 6:
                dayLabel = t("common:week.sat").toUpperCase();
                break;
            default:
                break;
        }
        return dayLabel;
    };

    const onChange = (v) => {
        onChangeClinician(index, v);
    };
    const getToTop = () => {
        return index === 5 || index === 6;
    };
    const getClinicianName = (clinicianID) => {
        if (options) {
            const clinician = options.find((c) => c.value === clinicianID);
            return clinician ? clinician.label : "";
        }
        return "";
    };

    const getFilterOptions = () => {
        if (options2.length >= index) {
            const opt = options2[index];
            if (opt) {
                return options.filter((o) => o.value !== opt);
            }
        }
        return options;
    };

    return (
        <div
            className={`tableRow ${!oneRow ? "oneRow" : ""} ${!labels ? "labels" : ""} ${changeSchedule? '' : 'onlyRead'}`}>
            {labels ? <div className="value">{getDayLabel()}</div> : ""}
            <div>
                {changeSchedule ? (
                    <Select
                        required={false}
                        value={item}
                        onChange={onChange}
                        placeholder={"Select"}
                        options={getFilterOptions()}
                        hasScroll={true}
                        toTop={getToTop()}
                        event={PAGES.PROGRAM_DETAIL}
                        eventParams={{index: index}}
                    />
                ) : (
                    <span>{getClinicianName(item)}</span>
                )}
            </div>
        </div>
    );
}

function DemographicEntry({label, value, type, bold}) {
    if (type === "value") {
        return (
            <div className="entry">
                <p className="label">{label}</p>
                <p className={bold ? "bold" : ""}>{value ? value : "--"}</p>
            </div>
        );
    }
    if (type === "valueLinks") {
        return (
            <div className="entry type-list">
                <p className="label">{label}</p>
                <div className="values">
                    {value.map((item) => (
                        <a
                            key={item.name}
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {item.name}
                        </a>
                    ))}
                    {value.length === 0 ? "--" : ""}
                </div>
            </div>
        );
    }
    if (type === "valueItems") {
        return (
            <div className="entry type-items">
                <p className="label">{label}</p>
                <div className="values">
                    {value.map((item, index) => (
                        <p key={index}>{item}</p>
                    ))}
                    {value.length === 0 ? "--" : ""}
                </div>
            </div>
        );
    }
    if (type === "valueGroups") {
        return (
            <div className="entry type-groups">
                <p className="label">{label}</p>
                <div className="values">
                    {value.map((item, index) => (
                        <span key={index}>
              {item.key} <b>{item.value}</b>
                            {index < value.length - 1 ? "\xa0|\xa0" : ""}{" "}
            </span>
                    ))}
                    {value.length === 0 ? "--" : ""}
                </div>
            </div>
        );
    }
    if (type === "valueTags") {
        const isMultiValue = (item) => {
            return item.search("::") !== -1;
        };
        const getValues = (item) => {
            return item.split("::");
        };
        return (
            <div className="entry type-tags">
                <p className="label">{label}</p>
                <div className="values">
                    {value.map((item, index) =>
                            isMultiValue(item) ? (
                                <span key={index}>
                {getValues(item)[0]} <b>{getValues(item)[1]}</b>
                                    {index < value.length - 1 ? "\xa0|\xa0" : ""}{" "}
              </span>
                            ) : (
                                <span key={index}>
                {item}
                                    {index < value.length - 1 ? "\xa0|\xa0" : ""}
              </span>
                            )
                    )}
                    {value.length === 0 ? "--" : ""}
                </div>
            </div>
        );
    }
    return "";
}

const _ReactTooltip = TooltipLog(ReactTooltip);

function ProgramDetail({currentUser, match}) {
    let { programId } = useParams();
    const {t} = useTranslation(["programs", "common"]);
    const [program, setProgram] = useState(null);
    const [managerOptions, setManagerOptions] = useState([]);
    const [clinicianOptions, setClinicianOptions] = useState([]);
    const [backupOptions, setBackupOptions] = useState([]);
    const [alertEmptySchedule, setAlertEmptySchedule] = useState(false);
    const [countPersonInProgram, setCountPersonInProgram] = useState(0);
    const navigate = useNavigate();
    const [openModalReport, setOpenModalReport] = useState(false);
    const [isAlertsModalPresent, setAlertsModalPresent] = useState(false);

    const dummyData = {
        vitals: [],
        onCallSchedules: [null, null, null, null, null, null, null],
        onBackupSchedules: [null, null, null, null, null, null, null],
    };

    const loadClinicianOptions = (usersClinical) => {
        let clinicianOnCallItems = [];
        if (!program.supervisorClinicianRequired) {
            clinicianOnCallItems.push({label: "--", value: null});
        }
        let clinicianBackupItems = [];
        if (!program.backupSchedulesRequired) {
            clinicianBackupItems.push({label: "--", value: null});
        }
        let managerItems = [];
        if (!program.callSchedulesRequired) {
            managerItems.push({label: "--", value: null});
        }
        usersClinical.forEach((user) => {
            if (!user.user.isDisabled) {
                if (user.role === "clinician") {
                    clinicianOnCallItems.push({
                        label: user.user.bio.firstname + " " + user.user.bio.lastname,
                        value: user._id,
                    });
                    clinicianBackupItems.push({
                        label: user.user.bio.firstname + " " + user.user.bio.lastname,
                        value: user._id,
                    });
                }
                if (user.role === "clinician" || user.role === "Admin") {
                    managerItems.push({
                        label: user.user.bio.firstname + " " + user.user.bio.lastname,
                        value: user._id,
                    });
                }
            }
        });
        managerItems = managerItems.sort((a, b) => a.label.localeCompare(b.label));
        setManagerOptions(managerItems);
        clinicianOnCallItems = clinicianOnCallItems.sort((a, b) =>
            a.label.localeCompare(b.label)
        );
        setClinicianOptions(clinicianOnCallItems);
        clinicianBackupItems = clinicianBackupItems.sort((a, b) =>
            a.label.localeCompare(b.label)
        );
        setBackupOptions(clinicianBackupItems);
    };

    const [personInProgram] = useLazyQuery(
        gqlSchemaLibrary.patients.countPersonInProgram,
        {
            onCompleted: (onPatients) => {
                if (!!onPatients.countPersonInProgram.length) {
                    const patientsByProgram = onPatients.countPersonInProgram.find(
                        (pbp) => pbp.program === programId
                    );
                    setCountPersonInProgram(
                        patientsByProgram ? patientsByProgram.count : 0
                    );
                }
            },
        }
    );

    const [getClinicians] = useLazyQuery(
        gqlSchemaLibrary.clinicians.queries.getCliniciansByOrg,
        {
            onCompleted: (getCliniciansByOrg) => {
                console.log(
                    "[User List] User Clinicians: ",
                    getCliniciansByOrg.getCliniciansByOrg
                );
                if (getCliniciansByOrg.getCliniciansByOrg) {
                    loadClinicianOptions(getCliniciansByOrg.getCliniciansByOrg);
                }
            },
            fetchPolicy: "cache-and-network",
        }
    );

    useQuery(gqlSchemaLibrary.programs.query.getProgramById, {
        variables: {
            id: programId,
        },
        onCompleted: ({getProgramById}) => {
            if (!getProgramById) {
                navigate(-1);
            }
            const p = {...dummyData, ...getProgramById};
            if (p.onCallSchedules.every((s) => !s)) {
                setAlertEmptySchedule(true);
            }
            setProgram(p);
            personInProgram({});
            getClinicians({});
        },
        fetchPolicy: "cache-and-network",
    });

    const [setOnCallSchedule] = useMutation(
        gqlSchemaLibrary.programs.mutation.setOnCallSchedules,
        {
            onCompleted: ({setOnCallSchedules}) => {
                console.log("setOnCallSchedules: ", setOnCallSchedules);
            },
        }
    );

    const [setOnBackupSchedule] = useMutation(
        gqlSchemaLibrary.programs.mutation.setOnBackupSchedules,
        {
            onCompleted: ({setOnBackupSchedules}) => {
                console.log("setOnBackupSchedules: ", setOnBackupSchedules);
            },
        }
    );

    const [setSupervisorClinician] = useMutation(
        gqlSchemaLibrary.programs.mutation.setSupervisor,
        {
            onCompleted: ({setSupervisor}) => {
                console.log("setSupervisor: ", setSupervisor);
            },
        }
    );

    if (!program) {
        return <SpinnerLoader/>;
    }

    const getVitalsLabels = () => {
        return getVitals()
            .map((v) => {
                if (v.value === "SPO2") {
                    return "SpO2";
                }
                return v.value;
            })
            .join(", ");
    };

    const onChangeClinician = (index, clinicianId) => {
        setAlertEmptySchedule(false);
        // Propagate change
        let _program = JSON.parse(JSON.stringify(program));
        _program.onCallSchedules[index] = clinicianId;
        setOnCallSchedule({
            variables: {
                schedule: _program.onCallSchedules.map((sch) => sch),
                programId: _program._id,
            },
        });
        setProgram(_program);
    };

    const onChangeBackup = (index, clinicianId) => {
        setAlertEmptySchedule(false);
        // Propagate change
        let _program = JSON.parse(JSON.stringify(program));
        _program.onBackupSchedules[index] = clinicianId;
        setOnBackupSchedule({
            variables: {
                schedule: _program.onBackupSchedules.map((sch) => sch),
                programId: _program._id,
            },
        });
        setProgram(_program);
    };

    const onChangeProgramManager = (clinicianId) => {
        setSupervisorClinician({
            variables: {
                clinicianId: clinicianId,
                programId: program._id,
            },
        });
        setProgram((prevState) => ({
            ...prevState,
            ["supervisorClinician"]: clinicianId,
        }));
    };

    const getProgramType = () => {
        if (!program.programType) {
            return t("clinical");
        }
        return program.programType === "clinical"
            ? t("clinical")
            : t("not-clinical");
    };

    const goToEditProgram = () => {
        navigate(`/programs/edit/${program._id}`);
    };

    const addBiometric = () => {
        navigate(`/programs/${program._id}/create/vital`);
    };

    if (!program) {
        return "";
    }

    const getDurationDays = () => {
        return program.duration / 1000 / 60 / 60 / 24;
    };

    const editProgram = () => {
        return currentUser.userRoles && currentUser.userRoles.programsBase.edit;
    };

    const changeSchedule = () => {
        return (
            currentUser.clinician.role === "admin" ||
            currentUser.clinician.role === "clinician"
        );
    };

    const getClinicianName = (clinicianID) => {
        if (clinicianOptions) {
            const clinician = clinicianOptions.find((c) => c.value === clinicianID);
            return clinician ? clinician.label : "";
        }
        return "";
    };

    const getScheduleMessage = () => {
        if (program.onCallSchedulesInfo) {
            return program.onCallSchedulesInfo;
        }
        return t("program-detail-text-1");
    };

    const getVitals = () => {
        return program.vitals;
    };

    const onOpenProgramReportModal = () => {
        setOpenModalReport(true);
    };

    const onCloseProgramReportModal = () => {
        setOpenModalReport(false);
    };

    const getTrackers = () => {
        return program.vitals.concat(program.trackers);
    }

    return (
        <div id="ProgramsBase">
            <div className="filter">
                <div className="programOptions">
                    <i className="material-icons">
                        <Link to="/programs">arrow_back_ios</Link>
                    </i>
                    <div className="progTitle">
                        <h3>{!!program && program.name ? program.name : ""}</h3>
                        <div className="_active">{t("active")}</div>
                    </div>
                </div>
            </div>

            <div className="programDetails">
                <div className="sideA scrollBar">
                    <div className="demographics card">
                        <div className="title">
                            <p>{t("general")}</p>
                            {editProgram() && countPersonInProgram === 0 ? (
                                <ItemMenu actionWillRedirect={false}>
                                    <>
                                        <div className="_option" onClick={goToEditProgram}>
                                            <p>{t("edit-program")}</p>
                                        </div>
                                    </>
                                </ItemMenu>
                            ) : (
                                ""
                            )}
                        </div>
                        <div className="table">
                            <div>
                                <DemographicEntry
                                    label={t("program-name")}
                                    value={program.name}
                                    type="value"
                                    bold={true}
                                />
                                <DemographicEntry
                                    label={t("type")}
                                    value={getProgramType()}
                                    type="value"
                                    bold={true}
                                />
                                <DemographicEntry
                                    label={t("description")}
                                    value={program.description}
                                    type="value"
                                />
                                <DemographicEntry
                                    label={t("duration")}
                                    value={getDurationDays() + " " + t("participants:days")}
                                    type="value"
                                    bold={true}
                                />
                                <div className="entry">
                                    <p className="label">{t("clinician-supervisor")}</p>
                                    {changeSchedule() ? (
                                        <Select
                                            value={program.supervisorClinician}
                                            label=""
                                            onChange={onChangeProgramManager}
                                            placeholder={"Select Clinician"}
                                            options={managerOptions}
                                            event={PAGES.PROGRAM_DETAIL}
                                        />
                                    ) : (
                                        <p>{getClinicianName(program.supervisorClinician)}</p>
                                    )}
                                </div>
                                {program.legalRequired ? (
                                    <DemographicEntry
                                        label={t("legal-requirements")}
                                        value={
                                            program.legalRequired
                                                ? t("participants-consents-upload")
                                                : ""
                                        }
                                        type="value"
                                    />
                                ) : (
                                    ""
                                )}
                                {program.populationParams &&
                                program.populationParams.length !== 0 ? (
                                    <DemographicEntry
                                        label={t("applicable-population")}
                                        value={
                                            program.populationParams ? program.populationParams : []
                                        }
                                        type="valueGroups"
                                    />
                                ) : (
                                    ""
                                )}
                                {program.includeCriteria &&
                                program.includeCriteria.length !== 0 ? (
                                    <DemographicEntry
                                        label={t("inclusion-criteria")}
                                        value={
                                            program.includeCriteria ? program.includeCriteria : []
                                        }
                                        type="valueTags"
                                    />
                                ) : (
                                    ""
                                )}
                                {program.excludeCriteria &&
                                program.excludeCriteria.length !== 0 ? (
                                    <DemographicEntry
                                        label={t("exclusion-criteria")}
                                        value={
                                            program.excludeCriteria ? program.excludeCriteria : []
                                        }
                                        type="valueTags"
                                    />
                                ) : (
                                    ""
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="demographics card vitals">
                        <div className="title">
                            <p>
                                <span className="counter">{getVitals().length}</span>{" "}
                                {t("trackers")}: {getVitalsLabels()}
                            </p>
                            {editProgram() && countPersonInProgram === 0 ? (
                                <div className="actionButton" onClick={addBiometric}>
                                    <ButtonIcon size={34}>
                                        <span className="material-icons">add</span>
                                    </ButtonIcon>
                                    <p>{t("add-biometric")}</p>
                                </div>
                            ) : (
                                ""
                            )}
                        </div>
                        <div className="trackersList">
                            {getTrackers().map((tracker, index) => (
                                <TrackerEntry
                                    key={index}
                                    vital={tracker}
                                    organizationId={program.organizationId}
                                />
                            ))}
                        </div>
                    </div>
                    <div className="metricsAndreports card ">
                        <div className="title">
                            <p>{t("Metrics and Reports")}</p>
                        </div>
                        <div className="trackersList">
                            <div className="downloadSection">
                                <_ReactTooltip
                                    className="popup"
                                    id="programs"
                                    place="bottom"
                                    multiline={true}
                                    backgroundColor="#516079"
                                    event={PAGES.PROGRAM_DETAIL}
                                />
                                <p
                                    data-for="programs"
                                    data-tip={
                                        t("programs:participants-modal-text").replace(
                                            "and",
                                            "<br>and"
                                        ) +
                                        " " +
                                        moment().format("MMMM Do YYYY, h:mm:ss a")
                                    }
                                    data-multiline={true}
                                >
                                    {t("participants-in-a-program")}
                                </p>
                                <button
                                    className="button primary"
                                    onClick={onOpenProgramReportModal}
                                >
                                    <span>{t("common:download")}</span>
                                </button>
                            </div>
                            <div className="downloadSection">
                                <_ReactTooltip
                                    id="alerts"
                                    className="popup"
                                    multiline={true}
                                    backgroundColor="#516079"
                                    event={PAGES.PROGRAM_DETAIL}
                                />
                                <p
                                    data-for="alerts"
                                    data-tip={
                                        t("programs:alerts-modal-text") +
                                        " " +
                                        moment().format("MMMM Do YYYY, h:mm:ss a")
                                    }
                                    data-multiline={true}
                                >
                                    {t("alerts-for-the-program")}
                                </p>
                                <button
                                    className="button primary"
                                    onClick={setAlertsModalPresent}
                                >
                                    <span>{t("common:download")}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="sideB">
                    <div className="programs card">
                        <div className="title">
                            <Link to={`/programs/${program._id}/participants`}>
                                <p>
                                    <span className="counter">{countPersonInProgram}</span>
                                    {countPersonInProgram === 1
                                        ? t("participant")
                                        : t("participants")}
                                </p>
                            </Link>
                        </div>
                        <div className="tableList">
                            <div className="tableHead">
                                <span>{t("this-month")}</span>
                                <span className="material-icons">expand_more</span>
                            </div>
                            <div></div>
                        </div>
                    </div>
                    <div className="schedule card">
                        <div className="title">
                            <div>
                                <p>{t("on-call-schedule")}</p>
                            </div>
                            <div className="_label">{getScheduleMessage()}</div>
                        </div>
                        <div className="table">
                            <div className="tableHead">{t("program-detail-text-2")}</div>
                            {alertEmptySchedule ? (
                                <div className="tableAlert">{t("program-detail-text-3")}</div>
                            ) : (
                                ""
                            )}
                            <div
                                className={`tableList ${
                                    !program.backupSchedulesEnabled ? "oneRow" : ""
                                }`}
                            >
                                {program.backupSchedulesEnabled ? (
                                    <>
                    <span className="containerList">
                      <div className="tableRow ">
                        <span/>
                        <span className="labels">{t("on-call")}</span>
                      </div>
                    </span>
                                        <span className="containerList">
                      <div className="tableRow labels">
                        <span/>
                        <span className="labels">{t("backup")}</span>
                      </div>
                    </span>
                                    </>
                                ) : (
                                    ""
                                )}
                                <div className="containerList">
                                    {program.onCallSchedules.map((item, index) => (
                                        <ScheduleEntry
                                            key={'OnCal-' + index}
                                            index={index}
                                            labels={true}
                                            oneRow={program.backupSchedulesEnabled}
                                            item={item}
                                            options={clinicianOptions}
                                            options2={
                                                program.onBackupSchedules
                                                    ? program.onBackupSchedules
                                                    : []
                                            }
                                            onChangeClinician={onChangeClinician}
                                            changeSchedule={changeSchedule()}
                                        />
                                    ))}
                                </div>
                                {program.backupSchedulesEnabled ? (
                                    <div className="containerList">
                                        {program.onBackupSchedules.map((item, index) => (
                                            <ScheduleEntry
                                                key={'OnBack-' + index}
                                                index={index}
                                                labels={false}
                                                oneRow={true}
                                                item={item}
                                                options={backupOptions}
                                                options2={program.onCallSchedules}
                                                onChangeClinician={onChangeBackup}
                                                changeSchedule={changeSchedule()}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    ""
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <ProgramReportDialog
                programId={program._id}
                programName={program.name}
                openModal={openModalReport}
                closeModal={onCloseProgramReportModal}
            />
            <AlertsReportDialog
                programId={program._id}
                programName={program.name}
                openModal={isAlertsModalPresent}
                closeModal={setAlertsModalPresent}
            />
        </div>
    );
}

export default PageViewLog(ProgramDetail);
